// ============================================================================
// StagePills
// ----------------------------------------------------------------------------
// The QuoteMaker top-nav LEFT cluster: the Stage 1->5 strip (aka "stage pills").
// Each pill shows the stage number (or a check when completed), highlights the
// active stage in brand orange, disables stages the user cannot navigate to,
// and collapses to just the number/check badge below the `lg` breakpoint
// (keeping the nav on one row when the window is too narrow for the words).
//
// Six visual states, in precedence order (the merge of two same-day fixes to
// the same misread: wms-cascade-handoff's fresh/notification state and
// elastic-greider's outline + dim-exclusivity — see both CHANGELOG entries):
//   active     — brand-orange fill, white label. You are here.
//   completed  — green tint + check (navigable; green carries the affordance).
//   fresh      — newly REACHABLE and never visited (ahead of the current
//                stage): the pill is a notification until first visit. Accent
//                breathing ring + accent number chip (the "unread count") via
//                .stage-btn[data-fresh] CSS, full-strength label, tooltip
//                "X is ready — click to open". Consumed by navigating there;
//                derived (canNav && !completed && ahead), never stored.
//   not run    — a stage the Quote Brain did not do on its job (Risks, Method;
//                lucid 017 O1): dashed outline, muted label, a minus in the
//                badge and a tooltip that says so. Not work left over, so
//                neither amber review nor a fresh invitation.
//   review     — available and behind the active stage, but not completed:
//                amber status treatment + stage number. This is actionable
//                unfinished work, not a disabled control.
//   navigable  — available fallback: full-strength label + hairline outline.
//   disabled   — dimmed label + 0.5 opacity, no outline. ONLY canNav=false
//                pills get the dimmed treatment: an available pill must never
//                read as blocked (operator misread an enabled Stage 5 pill as
//                greyed-out after a cascade run, 2026-07-28 — same failure
//                mode as the Stage 4->5 incident fixed in wms-cascade-handoff).
//
// TS-060 / DEC-COUNCIL-015 T3 (QuoteMakerNavBar sub-slice): extracted VERBATIM
// from App.jsx's render (behaviour-preserving; only type annotations, this
// header, and identifier->prop renames added). The full click-handler logic
// stays in App and arrives as the `onSelectStage(stageId)` callback prop (the
// T3 `onViewResult` pattern) -- no pipeline state moves into this presentational
// leaf. `STAGES` is a module-level constant this component imports directly.
// ============================================================================

import React from 'react';
import { VISIBLE_STAGES } from '../../config/brand';

interface StagePillsProps {
    /** The current pipeline stage (1-5). */
    stage: number;
    /** True while the Stage 5 quote editor is open (Stage 5 is a virtual stage). */
    quoteEditorOpen: boolean;
    /** The set of stage ids considered complete (App's `effectiveCompletedStages`). */
    completedStages: Set<number>;
    /** The current quote data (only its truthiness is read, to gate the Stage 5 check). */
    quoteData: unknown;
    /** Returns whether the user may navigate to the given stage id right now. */
    canNavigateToStage: (stageId: number) => boolean;
    /** True while a stage transition animation is running (disables all pills). */
    isStageTransitioning: boolean;
    /** True while risk analysis is running (drives the downstream-locked tooltip). */
    isAnalyzing: boolean;
    /** Fires when the user clicks a navigable stage pill; App owns the nav side effects. */
    onSelectStage: (stageId: number) => void;
    /** Stages the Quote Brain did not run on this job, keyed by id, with the tooltip that explains it. */
    notRunStages?: Readonly<Record<number, string>> | null;
}

export function StagePills({
    stage,
    quoteEditorOpen,
    completedStages,
    quoteData,
    canNavigateToStage,
    isStageTransitioning,
    isAnalyzing,
    onSelectStage,
    notRunStages = null,
}: StagePillsProps) {
    const activeStageId = quoteEditorOpen ? 5 : stage;
    return (
        <nav className="qm-stage-pills flex items-center gap-1 relative z-10" style={{ flex: '0 1 auto', minWidth: 0, flexWrap: 'nowrap' }} aria-label="Quote stages">
            {VISIBLE_STAGES.map((s, idx) => {
                const isActive = s.id === activeStageId;
                const isCompleted = completedStages.has(s.id) && (s.id !== 5 || !!quoteData);
                const canNav = canNavigateToStage(s.id);
                // FRESH: newly reachable and never been there. The pipeline has
                // opened a door AHEAD of the operator that he has not walked
                // through yet, and the pill's job flips from "state readout" to
                // "notification". Derived, not stored: a stage the operator is
                // past (id <= stage) has been seen, a completed one is done, a
                // non-navigable one is not an invitation. This exists because an
                // available-but-unvisited pill used to render nearly identically
                // to a disabled one (50% vs 30% white on dark), and an operator
                // read an ENABLED Stage 5 as blocked — the same
                // looks-blocked-when-it-isn't failure as the Stage 4→5 dialog.
                const notRunNote = !isActive && !isCompleted ? notRunStages?.[s.id] ?? null : null;
                const isNotRun = notRunNote !== null;
                const isFresh = canNav && !isActive && !isCompleted && !isNotRun && s.id > activeStageId;
                const needsReview = canNav && !isActive && !isCompleted && !isNotRun && !isFresh && s.id < activeStageId;
                const state = isActive
                    ? 'active'
                    : isCompleted
                        ? 'completed'
                        : isNotRun
                            ? 'not-run'
                        : isFresh
                            ? 'fresh'
                            : needsReview
                                ? 'review'
                                : canNav
                                    ? 'available'
                                    : 'disabled';
                const accessibleState = isActive
                    ? 'current stage'
                    : isCompleted
                        ? 'completed'
                        : isNotRun
                            ? 'not run'
                        : isFresh
                            ? 'ready'
                            : needsReview
                                ? 'needs review'
                                : canNav
                                    ? 'available'
                                    : 'locked';
                return (
                    <button key={s.id}
                        onClick={() => onSelectStage(s.id)}
                        className={`stage-btn rounded flex items-center justify-center font-bold uppercase transition-all h-[30px] gap-1 text-[10px] tracking-tight px-2 ${isActive ? 'stage-btn-active' : 'stage-btn-inactive'}`}
                        data-nav={canNav ? 'true' : undefined}
                        data-active={isActive ? 'true' : undefined}
                        data-fresh={isFresh ? 'true' : undefined}
                        data-state={state}
                        disabled={!canNav || isStageTransitioning}
                        aria-disabled={!canNav || isStageTransitioning}
                        aria-label={`Stage ${idx + 1}: ${s.name}, ${accessibleState}`}
                        aria-current={isActive ? 'step' : undefined}
                        style={{
                            // A fresh pill leaves background/border to the
                            // .stage-btn[data-fresh] CSS (the breathing ring and
                            // the badge dot cannot be inline anyway); everything
                            // else keeps the original inline treatment.
                            background: isActive ? 'var(--qm-accent)'
                                : isCompleted ? 'rgba(16,185,129,0.15)'
                                : isNotRun ? 'transparent'
                                : isFresh ? undefined
                                : needsReview ? 'color-mix(in oklab, var(--qm-status-warning) 12%, transparent)'
                                : 'rgba(255,255,255,0.05)',
                            color: isActive ? 'white'
                                : isCompleted ? '#6ee7b7'
                                // FRESH keeps its own brighter ink; a NAVIGABLE
                                // pill that is not fresh (behind the operator,
                                // revisitable) takes the elastic-greider grafts:
                                // 0.85 label + the hairline outline below. The
                                // 0.3 dim is EXCLUSIVE to canNav=false — an
                                // available pill must never read as blocked.
                                : isFresh ? 'rgba(255,255,255,0.92)'
                                : isNotRun ? 'rgba(255,255,255,0.6)'
                                : needsReview ? 'var(--qm-status-warning-light)'
                                : canNav ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)',
                            // Fresh leaves the border to the data-fresh CSS (the
                            // accent ring); other navigable pills carry the
                            // hairline outline (0.35 white clears the WCAG 3:1
                            // non-text floor on both ends of the navbar
                            // gradient; a full border, not a Rule-12a accent,
                            // and the 1px slot pre-existed so no layout shift).
                            ...(isFresh ? {} : {
                                border: isNotRun
                                    ? '1px dashed rgba(255,255,255,0.35)'
                                    : needsReview
                                    ? '1px solid color-mix(in oklab, var(--qm-status-warning) 65%, transparent)'
                                    : !isActive && !isCompleted && canNav
                                        ? '1px solid rgba(255,255,255,0.35)'
                                    : '1px solid transparent',
                            }),
                            cursor: canNav && !isStageTransitioning ? 'pointer' : 'not-allowed',
                            opacity: canNav || isActive ? 1 : 0.5,
                            ...(isActive ? { boxShadow: '0 1px 3px rgba(0,0,0,0.3)' } : {}),
                        }}
                        title={isNotRun ? notRunNote
                            : isFresh ? `${s.name} is ready — click to open`
                            : needsReview ? `${s.name} needs review — click to open`
                            : canNav && !isActive ? `Go to ${s.name}`
                            // 5C / O-S5: the locked copy belongs to a pill that IS locked. An enabled or active pill must not tell the operator it is blocked.
                            : (!canNav && isAnalyzing && s.id >= 3 ? 'Finish or stop the current Risk Analysis before opening downstream pricing stages.' : s.tooltip)}
                    >
                        <span
                            className="rounded flex items-center justify-center w-4 h-4 text-[10px]"
                            style={{
                                background: isActive ? 'rgba(255,255,255,0.2)'
                                    : isCompleted ? 'rgba(16,185,129,0.2)'
                                    // Fresh: the number chip carries the accent, the
                                    // way an unread count does on a notification.
                                    : isFresh ? 'var(--qm-accent)'
                                    : needsReview ? 'var(--qm-status-warning)'
                                    : 'rgba(255,255,255,0.1)',
                                ...((isFresh || needsReview) ? { color: needsReview ? '#1a1a1a' : '#fff' } : {}),
                            }}
                        >
                            {isCompleted && !isActive ? '\u2713' : isNotRun ? '\u2212' : idx + 1}
                        </span>
                        {/* Name shown at the `lg` breakpoint up; below it the
                            strip collapses to just the number/check badge so the
                            whole nav row stays on ONE line (showing the words
                            below ~880px wraps the right-hand actions to a second
                            row). The button's aria-label keeps the full name and
                            state available when this visible label is hidden;
                            title remains the pointer/touch hint.
                            NOTE: this `lg:inline` was a no-op from v5.46.58 until
                            the tailwind.config.js content glob was widened to
                            include .tsx — Tailwind never generated the utility
                            while the class lived only in this .tsx file, so the
                            words were hidden at EVERY width. */}
                        {/* `s.short`, not a last-word derivation. The old
                            `s.name.split(' ').pop()` rendered "Scope Builder" as
                            BUILDER on the chip a builder clicks, and "Data Entry"
                            as "Entry". See the STAGES comment in brand.js. */}
                        <span className="hidden lg:inline">{s.short || s.name}</span>
                    </button>
                );
            })}

            {/* Below 390px (Peter, 2026-10-14: "for really small resolutions",
                390px is the normal phone and 320px the smallest we design for)
                the five pills give way to this label, and the Back and Next
                buttons beside it move one stage at a time (top-navbar.css).
                At other widths it is not displayed, so it is read only there. */}
            <span className="qm-stage-compact">
                <span className="qm-stage-compact__num">{Math.max(1, VISIBLE_STAGES.findIndex(s => s.id === activeStageId) + 1)}/{VISIBLE_STAGES.length}</span>
                <span className="qm-stage-compact__name">{VISIBLE_STAGES.find(s => s.id === activeStageId)?.short || ''}</span>
            </span>
        </nav>
    );
}
